import { useCallback, useRef, useState } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';

/** Prevents duplicate loginRedirect calls before React re-renders (e.g. double-click). */
let entraLoginRedirectInFlight = false;

/** @internal test helper */
export function resetEntraLoginRedirectGuard() {
    entraLoginRedirectInFlight = false;
}

/**
 * Start a single MSAL login redirect. Ignores subsequent calls until redirect completes or fails.
 * @param {import('@azure/msal-browser').IPublicClientApplication} instance
 * @param {import('@azure/msal-browser').RedirectRequest} [request]
 * @returns {Promise<void>}
 */
export function startEntraLoginRedirect(instance, request = loginRequest) {
    if (entraLoginRedirectInFlight) {
        return Promise.resolve();
    }
    entraLoginRedirectInFlight = true;
    return instance.loginRedirect(request).catch((error) => {
        entraLoginRedirectInFlight = false;
        throw error;
    });
}

/**
 * @param {import('@azure/msal-browser').RedirectRequest} [request]
 */
export function useEntraLoginRedirect(request = loginRequest) {
    const { instance, inProgress } = useMsal();
    const localInFlightRef = useRef(false);
    const [loginStarted, setLoginStarted] = useState(false);

    const isLoginPending =
        loginStarted ||
        entraLoginRedirectInFlight ||
        inProgress !== InteractionStatus.None;

    const loginRedirect = useCallback(() => {
        if (localInFlightRef.current || entraLoginRedirectInFlight) {
            return Promise.resolve();
        }
        if (inProgress !== InteractionStatus.None) {
            return Promise.resolve();
        }

        localInFlightRef.current = true;
        setLoginStarted(true);

        return startEntraLoginRedirect(instance, request).catch((error) => {
            localInFlightRef.current = false;
            setLoginStarted(false);
            throw error;
        });
    }, [instance, inProgress, request]);

    return { loginRedirect, isLoginPending };
}
